import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { withTranslation } from 'react-i18next';
import { getCsrfToken } from '../../../utils';

const MAX_SIZE = 20 * 1024 * 1024;

// Manual lähete import: an email attachment, a photo of a paper lähete, or any
// file. Stored verbatim; parsing + matching happen asynchronously.
function UploadLaheteDialog({ t, open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setError(null);
    }
  }, [open]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: MAX_SIZE,
  });

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/api/inbound-delivery-notes/upload/', formData, {
        headers: {
          'X-CSRFToken': getCsrfToken(),
          'Content-Type': 'multipart/form-data',
        },
      });
      onUploaded?.(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || t('inboundDeliveries.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('inboundDeliveries.uploadTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('inboundDeliveries.uploadSubtitle')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {file ? (
          <Paper
            variant="outlined"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: '10px' }}
          >
            <DescriptionIcon sx={{ color: 'secondary.main' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('inboundDeliveries.uploadReady')}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setFile(null)} disabled={uploading}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        ) : (
          <Box
            {...getRootProps()}
            data-testid="lahete-upload-dropzone"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              p: { xs: 3, sm: 5 },
              textAlign: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: isDragActive ? 'secondary.main' : 'divider',
              borderRadius: '12px',
              bgcolor: isDragActive ? 'rgba(49, 130, 206, 0.06)' : 'background.default',
              transition: 'all 180ms',
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 44, color: isDragActive ? 'secondary.main' : 'text.secondary' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {isDragActive ? t('inboundDeliveries.uploadDropHere') : t('inboundDeliveries.uploadDragDrop')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('inboundDeliveries.uploadClickToSelect')}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
              {t('inboundDeliveries.uploadFormats')}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', mt: '2px' }} />
          <Typography variant="caption" color="text.secondary">
            {t('inboundDeliveries.uploadInfo')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={uploading}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          data-testid="lahete-upload-submit"
        >
          {t('inboundDeliveries.uploadSubmit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(UploadLaheteDialog);
